import type { ShipmentProductList } from '@/apis/types';

/** One product line as the edit drawer edits it; `serial_number` is the stored comma-separated text. */
export interface EditProductLine {
  id: string;
  quantity: number;
  serial_number?: string;
}

/** One `products` item of the shipment update payload (`ShipmentProductSerializer`). */
export interface ProductLinePayload {
  product_id: string;
  quantity: number;
  serial_number: string;
}

const isBlank = (value?: string | null): boolean => !value?.trim();

/** True when the lines came from an importer and can't be edited here: any lot or expiry. */
export function isImporterManaged(rows: ShipmentProductList[]): boolean {
  return rows.some(
    (row) =>
      !isBlank(row.lot_number) ||
      !isBlank(row.expiration_date) ||
      (row.lots?.length ?? 0) > 0
  );
}

/** Serials as typed, in the stored "a, b, c" form. */
export function normalizeSerials(text?: string | null): string {
  return (text ?? '')
    .split(',')
    .map((serial) => serial.trim())
    .filter(Boolean)
    .join(', ');
}

/** Editable lines from the listing rows, keyed by catalogue product id; serials fall back to `serial_preview` when the API predates `serial_number`. */
export function editLinesFromApi(
  rows: ShipmentProductList[]
): EditProductLine[] {
  return rows.flatMap((row) =>
    row.product_id
      ? [
          {
            id: row.product_id,
            quantity: row.quantity ?? 1,
            serial_number: normalizeSerials(
              row.serial_number ?? row.serial_preview?.join(', ')
            ),
          },
        ]
      : []
  );
}

const toPayload = (lines: EditProductLine[]): ProductLinePayload[] =>
  lines.map((line) => ({
    product_id: line.id,
    quantity: line.quantity,
    serial_number: normalizeSerials(line.serial_number),
  }));

/** The update's `products`, or undefined when the lines are unchanged so the save leaves them alone (the update replaces every line). */
export function productLinesPayload(
  initial: EditProductLine[],
  current: EditProductLine[]
): ProductLinePayload[] | undefined {
  const next = toPayload(current);
  return JSON.stringify(next) === JSON.stringify(toPayload(initial))
    ? undefined
    : next;
}
